import type { CatalogProduct } from '../types/catalog';
import { getCategoryDisplayLabel } from '../config/catalogData';

/**
 * Interfaz para representar la estructura de un producto crudo directamente desde Supabase
 * o después de una adaptación inicial de products.json.
 * La función transformRawProductToCatalogProduct espera este formato.
 */
export interface RawProductDataForSupabase { // Exportar y renombrar para claridad
  // Campos comunes entre products.json y Supabase
  id: string;
  name: string;
  description: string | null;
  price: number;
  image_url?: string | null; // Changed from imageUrl to match typical Supabase column names, can be null
  
  // Campos específicos de Supabase
  categories?: string[]; // Este es text[] en Supabase, contendrá los NUEVOS IDs de categoría
  brand?: string | null;
  stock?: number | null;
  icon?: string | null;
  industries?: string[] | null;
  caracteristicas?: string | null; // Campo directo en Supabase
  especificaciones?: string | null; // Campo directo en Supabase
  presentacion?: string | null; // Campo directo en Supabase
  documentation?: string | null; // Renombrado a documentationUrl en CatalogProduct

  // Campo específico de products.json (para migración)
  originalCategory_from_json?: string; // Para diferenciar de 'categories' de Supabase
  technicalSpecs_from_json?: { // Para diferenciar de campos directos de Supabase
    Características?: string;
    Especificaciones?: string;
    Presentación?: string;
  };
   // Permite otras propiedades no definidas explícitamente
  [key: string]: any;
}

/**
 * Verifica si una cadena es una URL válida.
 * @param str La cadena a verificar.
 * @returns True si es una URL válida, false en caso contrario.
 */
function isValidHttpUrl(str: string): boolean {
  let url;
  try {
    url = new URL(str);
  } catch (_) {
    return false;
  }
  return url.protocol === "http:" || url.protocol === "https:";
}

import officialProductImages from '../data/officialProductImages.json';

/**
 * Transforma un objeto de producto crudo al formato CatalogProduct.
 * @param rawProduct El objeto de producto crudo.
 * @returns Un objeto CatalogProduct transformado.
 */
export function transformRawProductToCatalogProduct(rawProduct: RawProductDataForSupabase): CatalogProduct {
  let currentDescription = rawProduct.description;
  // Usar image_url de la BD, o el mapa oficial si en la BD aún figura null
  let currentImageUrl = rawProduct.image_url || (officialProductImages as Record<string, string>)[rawProduct.id] || null;

  // Si no hay imageUrl explícito y la descripción (de products.json) parece una URL, usarla como imageUrl
  // Para datos de Supabase, image_url ya debería estar poblado correctamente.
  if (!currentImageUrl && rawProduct.originalCategory_from_json && rawProduct.description && isValidHttpUrl(rawProduct.description)) {
    currentImageUrl = rawProduct.description;
  }
  
  // Determinar technicalSpecs y asegurar que no sea undefined
  // También asegurar que sus subcampos tengan valores por defecto si se usan para poblar campos directos
  let determinedTechnicalSpecs: { caracteristicas?: string; especificaciones?: string; presentacion?: string; } = {};

  if (rawProduct.technicalSpecs_from_json) {
    determinedTechnicalSpecs = {
      caracteristicas: rawProduct.technicalSpecs_from_json.Características || '',
      especificaciones: rawProduct.technicalSpecs_from_json.Especificaciones || '',
      presentacion: rawProduct.technicalSpecs_from_json.Presentación || '',
    };
  } else if (rawProduct.caracteristicas || rawProduct.especificaciones || rawProduct.presentacion) {
    determinedTechnicalSpecs = {
      caracteristicas: rawProduct.caracteristicas || '',
      especificaciones: rawProduct.especificaciones || '',
      presentacion: rawProduct.presentacion || '',
    };
  }

  const catalogProduct: CatalogProduct = {
    id: rawProduct.id,
    name: rawProduct.name || '',
    description: currentDescription || '',
    price: String(rawProduct.price || 0),
    image_url: currentImageUrl || '',
    imageUrl: currentImageUrl || '',
    brand: rawProduct.brand || '',
    stock: rawProduct.stock || 0,
    icon: rawProduct.icon || '', // Asumiendo que icon también podría necesitar un default si se hace requerido
    industries: rawProduct.industries || [],
    originalCategory_from_json: rawProduct.originalCategory_from_json,
    categoryIds: rawProduct.categories || [],
    technicalSpecs: determinedTechnicalSpecs, // Ahora siempre es un objeto
    documentacion: rawProduct.documentation || '',
    category: '', // Will be set by assignNewCategoryIds
    code: rawProduct.code || '',
    normative: rawProduct.normative || '',
    featured_status: rawProduct.featured_status, // Este es opcional, así que está bien si es undefined
    
    // Poblar los campos directos requeridos desde determinedTechnicalSpecs
    características: determinedTechnicalSpecs.caracteristicas || '',
    especificaciones: determinedTechnicalSpecs.especificaciones || '',
    presentación: determinedTechnicalSpecs.presentacion || '',
  };
  
  // La lógica de poblar características, etc., desde technicalSpecs ya está integrada arriba.

  // Siempre llamar a assignNewCategoryIds para procesar y mapear categoryIds
  // y establecer el campo 'category' correctamente.
  return assignNewCategoryIds(catalogProduct);
}

/**
 * Mapeo exhaustivo de compatibilidad para transformar nombres antiguos,
 * nombres descriptivos y categorías previas a los nuevos IDs de catalogStructure.
 */
const categoryMapping: Record<string, string[]> = {
  // Calzado
  "Calzado": ["calzado", "calzado-botines"],
  "calzado": ["calzado"],
  "corporal-calzado": ["calzado", "calzado-botines"],
  "calzado-zapatos": ["calzado", "calzado-zapatos"],
  "calzado-botinas": ["calzado", "calzado-botines"],
  "calzado-botines": ["calzado", "calzado-botines"],
  "calzado-zapatillas": ["calzado", "calzado-zapatillas"],
  "calzado-botas": ["calzado", "calzado-botas"],
  "calzado-botas-pvc": ["calzado", "calzado-botas"],

  // Indumentaria
  "Indumentaria": ["indumentaria", "indumentaria-ropa-trabajo"],
  "indumentaria": ["indumentaria"],
  "corporal-indumentaria": ["indumentaria", "indumentaria-ropa-trabajo"],
  "indumentaria-ropa-trabajo": ["indumentaria", "indumentaria-ropa-trabajo"],
  "indumentaria-camisas-remeras": ["indumentaria", "indumentaria-ropa-trabajo"],
  "indumentaria-pantalones": ["indumentaria", "indumentaria-ropa-trabajo"],
  "indumentaria-camperas": ["indumentaria", "indumentaria-abrigo"],
  "indumentaria-buzos": ["indumentaria", "indumentaria-abrigo"],
  "indumentaria-termica": ["indumentaria", "indumentaria-abrigo"],
  "indumentaria-descartables": ["indumentaria", "indumentaria-descartables"],
  "indumentaria-quimica": ["indumentaria", "indumentaria-quimica"],
  "indumentaria-soldadura": ["indumentaria", "indumentaria-soldadura"],
  "indumentaria-ignifuga": ["indumentaria", "indumentaria-ignifuga-arco"],
  "contrafuego-indumentaria": ["indumentaria", "indumentaria-ignifuga-arco"],
  "contra-fuego": ["indumentaria", "indumentaria-ignifuga-arco"],

  // EPP Craneana
  "Protección": ["epp", "epp-craneana", "craneana-cascos"],
  "craneana": ["epp", "epp-craneana"],
  "proteccion-craneana": ["epp", "epp-craneana", "craneana-cascos"],
  "craneana-cascos": ["epp", "epp-craneana", "craneana-cascos"],
  "craneana-dielectricos": ["epp", "epp-craneana", "craneana-cascos"],
  "craneana-altura": ["epp", "epp-craneana", "craneana-cascos"],
  "craneana-general": ["epp", "epp-craneana", "craneana-cascos"],
  "craneana-accesorios": ["epp", "epp-craneana", "craneana-accesorios"],

  // EPP Ocular y Facial
  "ocular": ["epp", "epp-ocular"],
  "proteccion-ocular": ["epp", "epp-ocular"],
  "ocular-anteojos": ["epp", "epp-ocular", "ocular-anteojos"],
  "ocular-antiparras": ["epp", "epp-ocular", "ocular-antiparras"],
  "facial": ["epp", "epp-facial"],
  "facial-protectores": ["epp", "epp-facial", "facial-protectores"],
  "facial-accesorios": ["epp", "epp-facial", "facial-accesorios"],
  "facial-soldador": ["epp", "epp-facial", "facial-protectores"],

  // EPP Respiratoria
  "respiratoria": ["epp", "epp-respiratoria"],
  "proteccion-respiratoria": ["epp", "epp-respiratoria"],
  "respiratoria-desechables": ["epp", "epp-respiratoria", "respiratoria-desechables"],
  "respiratoria-mascarillas-descartables": ["epp", "epp-respiratoria", "respiratoria-desechables"],
  "respiratoria-reutilizables": ["epp", "epp-respiratoria", "respiratoria-reutilizables"],
  "respiratoria-mascaras": ["epp", "epp-respiratoria", "respiratoria-reutilizables"],
  "respiratoria-autonomos": ["epp", "epp-respiratoria", "respiratoria-autonomos"],
  "respiratoria-equipos-autonomos": ["epp", "epp-respiratoria", "respiratoria-autonomos"],

  // EPP Manos / Guantes
  "Seguridad Industrial": ["epp", "epp-manos"],
  "manos": ["epp", "epp-manos"],
  "proteccion-manos": ["epp", "epp-manos"],
  "manos-descartables": ["epp", "epp-manos", "manos-descartables"],
  "manos-latex": ["epp", "epp-manos", "manos-latex"],
  "manos-nitrilo": ["epp", "epp-manos", "manos-nitrilo"],
  "manos-nitrilicos": ["epp", "epp-manos", "manos-nitrilo"],
  "manos-pvc": ["epp", "epp-manos", "manos-pvc"],
  "manos-anticorte": ["epp", "epp-manos", "manos-anticorte"],
  "manos-corte": ["epp", "epp-manos", "manos-anticorte"],
  "manos-temperatura": ["epp", "epp-manos", "manos-temperatura"],
  "manos-termicos": ["epp", "epp-manos", "manos-temperatura"],
  "manos-tejidos": ["epp", "epp-manos", "manos-tejidos"],
  "manos-mecanicos": ["epp", "epp-manos", "manos-tejidos"],
  "manos-dielectricos": ["epp", "epp-manos", "manos-dielectricos"],

  // EPP Auditiva
  "auditiva": ["epp", "epp-auditiva"],
  "proteccion-auditiva": ["epp", "epp-auditiva"],
  "auditiva-copa": ["epp", "epp-auditiva", "auditiva-copa"],
  "auditiva-copa-vincha": ["epp", "epp-auditiva", "auditiva-copa"],
  "auditiva-endoaural": ["epp", "epp-auditiva", "auditiva-endoaural"],
  "auditiva-expansibles": ["epp", "epp-auditiva", "auditiva-endoaural"],

  // Altura
  "altura": ["altura"],
  "altura-epp": ["altura"],
  "altura-arnes": ["altura", "altura-arneses"],
  "altura-arneses": ["altura", "altura-arneses"],
  "altura-colas-amarre": ["altura", "altura-colas-amarre"],
  "altura-cabo-vida": ["altura", "altura-colas-amarre"],
  "altura-retractiles": ["altura", "altura-retractiles"],
  "altura-lineas-vida": ["altura", "altura-lineas-vida"],
  "altura-anticaida": ["altura", "altura-retractiles"],

  // Izaje
  "izaje": ["izaje"],
  "izaje-equipos": ["izaje", "izaje-equipos"],
  "izaje-eslingas": ["izaje", "izaje-eslingas"],
  "izaje-cintas-crique": ["izaje", "izaje-cintas-crique"],

  // Señalización
  "senalizacion": ["senalizacion"],
  "senalizacion-vial": ["senalizacion", "senalizacion-conos"],
  "senalizacion-conos": ["senalizacion", "senalizacion-conos"],
  "conos-barreras": ["senalizacion", "senalizacion-conos"],
  "senalizacion-cadenas": ["senalizacion", "senalizacion-cadenas"],
  "senalizacion-delineadores-postes": ["senalizacion", "senalizacion-delineadores-postes"],
  "senalizacion-mallas": ["senalizacion", "senalizacion-mallas"],
  "senalizacion-cintas": ["senalizacion", "senalizacion-cintas"],
  "senalizacion-vehicular": ["senalizacion", "senalizacion-vehicular"],
  "senalizacion-carteleria": ["senalizacion", "senalizacion-carteleria"],

  // Emergencia
  "seguridad-emergencias": ["emergencia"],
  "primeros-auxilios": ["emergencia", "emergencia-botiquines"],
  "botiquines": ["emergencia", "emergencia-botiquines"],
  "emergencia-botiquines": ["emergencia", "emergencia-botiquines"],
  "emergencia-duchas": ["emergencia", "emergencia-duchas"],
  "emergencia-lavaojos": ["emergencia", "emergencia-lavaojos"],
  "emergencia-luces": ["emergencia", "emergencia-luces"],
  "extincion": ["emergencia"],

  // Bloqueo y Seguridad (LOTO)
  "bloqueo": ["bloqueo"],
  "bloqueo-sistemas": ["bloqueo", "bloqueo-sistemas"],
  "bloqueo-candados": ["bloqueo", "bloqueo-candados"],
  "bloqueo-estaciones-armarios": ["bloqueo", "bloqueo-estaciones-armarios"],
  "bloqueo-accesorios": ["bloqueo", "bloqueo-accesorios"],

  // Derrames y Limpieza
  "limpieza-mantenimiento": ["derrames-limpieza"],
  "derrames-limpieza": ["derrames-limpieza"],
  "absorbentes-industriales": ["derrames-limpieza", "derrames-absorbentes"],
  "derrames-absorbentes": ["derrames-limpieza", "derrames-absorbentes"],
  "derrames-panos": ["derrames-limpieza", "derrames-panos"],
  "derrames-recipientes": ["derrames-limpieza", "derrames-recipientes"],

  // Accesorios
  "Seguridad": ["accesorios"],
  "accesorios": ["accesorios"],
  "accesorios-cutters": ["accesorios", "accesorios-cutters"],
  "accesorios-linternas": ["accesorios", "accesorios-linternas"],
  "accesorios-espejos": ["accesorios", "accesorios-espejos"],
  "accesorios-cintas-antideslizantes": ["accesorios", "accesorios-cintas-antideslizantes"]
};

/**
 * Asigna las nuevas IDs de categoría a un producto.
 * Procesa `originalCategory_from_json` y también los `categoryIds` existentes
 * (que podrían ser nombres antiguos de `rawProduct.categories`).
 * Actualiza `product.categoryIds` y `product.category`.
 * @param product El producto a actualizar.
 * @returns El producto con `categoryIds` y `category` actualizados.
 */
export function assignNewCategoryIds(product: CatalogProduct): CatalogProduct {
  const currentIdsFromProduct = product.categoryIds || [];
  const newIdsSet = new Set<string>();

  // 1. Intentar mapear desde originalCategory_from_json si está presente
  if (product.originalCategory_from_json) {
    const mappedFromOriginal = categoryMapping[product.originalCategory_from_json];
    if (mappedFromOriginal) {
      mappedFromOriginal.forEach(id => newIdsSet.add(id));
    } else {
      console.warn(`No category mapping found for originalCategory_from_json: "${product.originalCategory_from_json}" for product "${product.name}"`);
      // Si no hay mapeo para originalCategory_from_json, pero es un string, podría ser un ID válido o un nombre antiguo.
      // Lo procesaremos en el siguiente paso si también está en currentIdsFromProduct o si currentIdsFromProduct está vacío.
    }
  }

  // 2. Procesar los IDs/nombres existentes en product.categoryIds (que vienen de rawProduct.categories)
  // Estos podrían ser ya IDs nuevos, o nombres antiguos que necesitan mapeo.
  currentIdsFromProduct.forEach(idOrName => {
    const mappedFromCurrent = categoryMapping[idOrName];
    if (mappedFromCurrent) { // Si es un nombre antiguo conocido en el mapping
      mappedFromCurrent.forEach(id => newIdsSet.add(id));
    } else { // Si no está en el mapping, asumir que es un ID nuevo (o un nombre antiguo no mapeable)
      newIdsSet.add(idOrName);
    }
  });
  
  // Si después de procesar originalCategory y currentIds, newIdsSet está vacío,
  // y originalCategory_from_json existe pero no se mapeó, añadirlo tal cual
  // como último recurso (podría ser un ID nuevo que no estaba en categoryMapping).
  if (newIdsSet.size === 0 && product.originalCategory_from_json && !categoryMapping[product.originalCategory_from_json]) {
    newIdsSet.add(product.originalCategory_from_json);
  }

  product.categoryIds = Array.from(newIdsSet);

  // 3. Actualizar el campo 'category' del producto con el nombre legible adecuado
  product.category = getCategoryDisplayLabel(product.categoryIds);

  return product;
}

// Ejemplo de uso (para demostración, lo eliminarías en producción aquí):
/*
const rawSampleProduct = {
  _id: "67bfd5f41e6fa9c1ff1a2694",
  name: "Botas de seguridad",
  description: "Botas con punta de acero para protección",
  price: 50,
  category: "Calzado",
  technicalSpecs: {
    "Características": "Botas de seguridad con puntera reforzada, suela antideslizante y material resistente al agua.",
    "Especificaciones": "Talla: 42, Material: Cuero sintético, Color: Negro.",
    "Presentación": "Caja de 1 par de botas."
  },
  documentation: "/path/to/technical-sheet.pdf"
};

const transformedProduct = transformRawProductToCatalogProduct(rawSampleProduct);
console.log("Transformed Product:", transformedProduct);

const productWithNewCategories = assignNewCategoryIds(transformedProduct);
console.log("Product with New Categories:", productWithNewCategories);
*/